import "@shopify/polaris/build/esm/styles.css";
import enTranslations from "@shopify/polaris/locales/en.json";
import deTranslations from "@shopify/polaris/locales/de.json";
import jaTranslations from "@shopify/polaris/locales/ja.json";
import csTranslations from "@shopify/polaris/locales/cs.json";
import daTranslations from "@shopify/polaris/locales/da.json";
import esTranslations from "@shopify/polaris/locales/es.json";
import fiTranslations from "@shopify/polaris/locales/fi.json";
import frTranslations from "@shopify/polaris/locales/fr.json";
import itTranslations from "@shopify/polaris/locales/it.json";
import koTranslations from "@shopify/polaris/locales/ko.json";
import nbTranslations from "@shopify/polaris/locales/nb.json";
import nlTranslations from "@shopify/polaris/locales/nl.json";
import plTranslations from "@shopify/polaris/locales/pl.json";
import ptBrTranslations from "@shopify/polaris/locales/pt-BR.json";
import ptPtTranslations from "@shopify/polaris/locales/pt-PT.json";
import svTranslations from "@shopify/polaris/locales/sv.json";
import thTranslations from "@shopify/polaris/locales/th.json";
import trTranslations from "@shopify/polaris/locales/tr.json";
import viTranslations from "@shopify/polaris/locales/vi.json";
import zhCnTranslations from "@shopify/polaris/locales/zh-CN.json";
import zhTwTranslations from "@shopify/polaris/locales/zh-TW.json";

import { AppProvider as PolarisProvider } from "@shopify/polaris";
import { StrictMode, useCallback, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App";
import { AppBridgeProvider } from "./AppBridgeProvider";
import { ShopGenerationBoundary } from "./ShopGenerationBoundary";
import { initLocale, loadLocaleDictionary, type Locale } from "./lib/i18n";
import { setCurrentLocale, setLocalePreference, type LocalePreference } from "./lib/i18n";
import { LocaleChangeContext } from "./lib/LocaleChangeContext";
import { captureShopifyEntryContext } from "./utils/shopifyEntryContext";

// Pull `host` from query string per Shopify embedded app convention.
const params = new URLSearchParams(window.location.search);
const host = params.get("host");
captureShopifyEntryContext(window.location.search);

// Shopifyの言語設定を既定にし、店ごとの明示選択を優先する。
const initialLocale = initLocale(window.location.search);

const apiKey = import.meta.env.VITE_SHOPIFY_API_KEY ?? "";

const polarisTranslations: Partial<Record<Locale, typeof enTranslations>> = {
  ja: jaTranslations,
  en: enTranslations,
  de: deTranslations,
  cs: csTranslations,
  da: daTranslations,
  es: esTranslations,
  fi: fiTranslations,
  fr: frTranslations,
  it: itTranslations,
  ko: koTranslations,
  nb: nbTranslations,
  nl: nlTranslations,
  pl: plTranslations,
  "pt-BR": ptBrTranslations,
  "pt-PT": ptPtTranslations,
  sv: svTranslations,
  th: thTranslations,
  tr: trTranslations,
  vi: viTranslations,
  "zh-CN": zhCnTranslations,
  "zh-TW": zhTwTranslations,
};
const additionalPolarisTranslations = import.meta.glob<typeof enTranslations>(
  "./lib/polaris-locale.*.json",
  { import: "default" },
);

async function loadPolarisTranslations(locale: Locale): Promise<typeof enTranslations | null> {
  const packaged = polarisTranslations[locale];
  if (packaged) return packaged;
  const load = additionalPolarisTranslations[`./lib/polaris-locale.${locale}.json`];
  return load ? load() : null;
}

let bootPolarisTranslations: typeof enTranslations = enTranslations;

function RootApp() {
  const [polarisI18n, setPolarisI18n] = useState(bootPolarisTranslations);
  const [revision, setRevision] = useState(0);
  const latestChange = useRef(0);
  const changeLocale = useCallback((preference: LocalePreference): boolean => {
    setLocalePreference(window.location.search, preference);
    const nextLocale = preference === "auto" ? initLocale(window.location.search) : setCurrentLocale(preference);
    const change = ++latestChange.current;
    void Promise.all([
      loadLocaleDictionary(nextLocale),
      loadPolarisTranslations(nextLocale),
    ]).then(([loaded, polaris]) => {
      if (change !== latestChange.current) return;
      if (!loaded || !polaris) setCurrentLocale("en");
      setPolarisI18n(loaded && polaris ? polaris : enTranslations);
      setRevision((value) => value + 1);
    }).catch(() => {
      if (change !== latestChange.current) return;
      setCurrentLocale("en");
      setPolarisI18n(enTranslations);
      setRevision((value) => value + 1);
    });
    return true;
  }, []);

  return (
    <LocaleChangeContext.Provider value={changeLocale}>
      <PolarisProvider key={revision} i18n={polarisI18n}>
        <AppBridgeProvider apiKey={apiKey} host={host}>
          <BrowserRouter>
            <ShopGenerationBoundary>
              <App />
            </ShopGenerationBoundary>
          </BrowserRouter>
        </AppBridgeProvider>
      </PolarisProvider>
    </LocaleChangeContext.Provider>
  );
}

void Promise.all([
  loadLocaleDictionary(initialLocale),
  loadPolarisTranslations(initialLocale),
]).then(([loaded, polaris]) => {
  if (loaded && polaris) bootPolarisTranslations = polaris;
  else setCurrentLocale("en");
}).catch(() => {
  setCurrentLocale("en");
}).finally(() => {
  const root = createRoot(document.getElementById("root")!);
  root.render(
    <StrictMode>
      <RootApp />
    </StrictMode>,
  );
});
